package fyi.moko.hotpl.data.repository

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceIdGenerator
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.RandomPlaceIdGenerator
import fyi.moko.hotpl.domain.model.PlaceVisitSummary
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.model.SyncAction
import fyi.moko.hotpl.domain.model.SyncQueueItem
import fyi.moko.hotpl.domain.model.PhotoRef
import fyi.moko.hotpl.domain.model.withDerivedCover
import fyi.moko.hotpl.domain.model.SyncState
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.repository.PlaceRepository
import fyi.moko.hotpl.domain.model.SYNC_ENTITY_PHOTO
import fyi.moko.hotpl.domain.service.PhotoSyncQueue
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.DuplicateKey
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlin.time.Clock

class JsPlaceRepositoryImpl(
    private val authService: AuthService = DummyAuthService(),
    private val now: () -> Long = { Clock.System.now().toEpochMilliseconds() },
    private val idGenerator: PlaceIdGenerator = RandomPlaceIdGenerator,
) : PlaceRepository, PhotoSyncQueue {

    private val places = MutableStateFlow<Map<String, Place>>(emptyMap())
    private val visits = MutableStateFlow<Map<String, Visit>>(emptyMap())
    private val syncQueue = MutableStateFlow<Map<Long, JsSyncQueueEntry>>(emptyMap())

    /**
     * The userId under which the in-memory maps are currently materialized. Updated
     * atomically during a [switchAccount] so that [persist] always writes to the
     * correct per-account localStorage key.
     */
    private var activeUserId: String? = null
    private var hasInitialized = false

    /** Monotonic counter for sync queue entry ids (JS has no autoincrement). */
    private var queueSeq: Long = 0L

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Unconfined)

    init {
        // Rehydrate from durable browser storage for the initial user so data
        // survives a refresh. The first [authService.currentUserId] emission seeds
        // [activeUserId] and loads the matching partition; subsequent emissions
        // (login / logout / switch) atomically swap partitions via [switchAccount].
        scope.launch {
            authService.currentUserId
                .collect { newUid ->
                    if (!hasInitialized) {
                        // First emission — initial load from the matching partition.
                        hasInitialized = true
                        activeUserId = newUid
                        rehydrateFromStore(newUid)
                    } else if (newUid != activeUserId) {
                        switchAccount(newUid)
                    }
                }
        }
    }

    /**
     * Atomically (1) persists the current in-memory snapshot under the *old* user's
     * key, (2) clears the in-memory maps, (3) rehydrates from the *new* user's key.
     * This ensures account A's local data stays in account A's partition and isn't
     * adopted into account B after a sign-in switch.
     */
    private fun switchAccount(newUid: String?) {
        // 1) Persist current data under the old key so nothing is lost.
        persist()

        // 2) Clear in-memory state.
        places.value = emptyMap()
        visits.value = emptyMap()
        syncQueue.value = emptyMap()
        queueSeq = 0L

        // 3) Rehydrate from the new key.
        activeUserId = newUid
        rehydrateFromStore(newUid)
    }

    private fun rehydrateFromStore(userId: String?) {
        JsPlaceStore.load(userId)?.let { snapshot ->
            // Merge rather than replace: the first currentUserId emission is async
            // (Firebase authStateChanged), so a savePlace() racing it can land in the
            // in-memory maps before this snapshot loads. In-memory wins on id clash —
            // it is strictly newer than anything on disk. (switchAccount clears the
            // maps first, so an account swap still gets a clean replace.)
            val racedQueueEntries = syncQueue.value.values.toList()
            places.value = snapshot.places.associateBy { it.id } + places.value
            visits.value = snapshot.visits.associateBy { it.id } + visits.value
            queueSeq = snapshot.syncQueue.maxOfOrNull { it.queueId } ?: 0L
            val mergedQueue = snapshot.syncQueue.associateBy { it.queueId }.toMutableMap()
            // Re-key raced entries above the disk max so they can't collide with (and
            // silently drop) a persisted queue entry.
            for (entry in racedQueueEntries) {
                val newId = ++queueSeq
                mergedQueue[newId] = entry.copy(queueId = newId)
            }
            syncQueue.value = mergedQueue
        }
        // Persist even if empty — writes the new partition key so a subsequent
        // savePlace lands in the right slot.
        persist()
    }

    /** Set the places map and persist the whole snapshot. */
    private fun updatePlaces(newValue: Map<String, Place>) {
        places.value = newValue
        persist()
    }

    /** Set the visits map and persist the whole snapshot. */
    private fun updateVisits(newValue: Map<String, Visit>) {
        visits.value = newValue
        persist()
    }

    private fun updateSyncQueue(newValue: Map<Long, JsSyncQueueEntry>) {
        syncQueue.value = newValue
        persist()
    }

    private fun persist() {
        JsPlaceStore.save(
            userId = activeUserId,
            snapshot = JsPlacesSnapshot(
                places = places.value.values.toList(),
                visits = visits.value.values.toList(),
                syncQueue = syncQueue.value.values.toList(),
            ),
        )
    }

    /**
     * Enqueues a sync queue entry for a place, mirroring Room's `enqueueCoalesced`
     * coalescing rules:
     * - Same entity + same action → skip (idempotent).
     * - Same entity, existing UPSERT + new DELETE → cancel-out: remove the pending
     *   UPSERT (the place was never pushed, so there's nothing to delete remotely).
     *   Only applies when the place's syncState is still PENDING_UPSERT (not yet
     *   pushed).
     * - Same entity, existing DELETE + new UPSERT → keep the DELETE (canonical
     *   action preserved), update the queue entry's action to UPSERT so the push
     *   engine re-evaluates the place's isDeleted flag.
     * - Otherwise → insert a new entry.
     */
    private fun enqueueSyncItem(entityId: String, action: SyncAction) {
        val timestamp = now()
        val currentQueue = syncQueue.value.toMutableMap()
        val existingEntry = currentQueue.values.find { it.entityId == entityId }

        if (existingEntry == null) {
            val newId = ++queueSeq
            currentQueue[newId] = JsSyncQueueEntry(
                queueId = newId,
                entityType = "PLACE",
                entityId = entityId,
                action = action.name,
                createdAt = timestamp,
            )
            updateSyncQueue(currentQueue)
            return
        }

        val existingAction = runCatching { SyncAction.valueOf(existingEntry.action) }
            .getOrDefault(SyncAction.UPSERT)

        if (existingAction == action) {
            // Idempotent — same action already queued.
            return
        }

        // Coalescing: cancel-out or action upgrade.
        if (existingAction == SyncAction.UPSERT && action == SyncAction.DELETE) {
            // UPSERT still pending (never pushed) + now DELETE → cancel-out.
            val place = places.value[entityId]
            if (place?.syncState == SyncState.PENDING_UPSERT) {
                currentQueue.remove(existingEntry.queueId)
                updateSyncQueue(currentQueue)
                return
            }
        }

        // DELETE → UPSERT upgrade or UPSERT → DELETE upgrade: replace the action
        // so the push engine re-evaluates the place's current state (isDeleted).
        currentQueue[existingEntry.queueId] = existingEntry.copy(action = action.name)
        updateSyncQueue(currentQueue)
    }

    override fun observePlaces(): Flow<List<Place>> =
        places.map { it.values.filter { p -> !p.isDeleted }.sortedByDescending { p -> p.updatedAt } }

    override fun observeVisitSummaries(): Flow<List<PlaceVisitSummary>> =
        visits.map { allVisits ->
            allVisits.values.filter { !it.isDeleted }.groupBy { it.placeId }.map { (placeId, visits) ->
                val lastVisit = visits.maxByOrNull { it.visitedAt }
                PlaceVisitSummary(
                    placeId = placeId,
                    visitCount = visits.size,
                    lastVisitedAt = lastVisit?.visitedAt ?: 0L,
                    emotion = Emotion.fromId(lastVisit?.emotion),
                )
            }
        }

    override fun observePlace(id: String): Flow<Place?> =
        places.map { val p = it[id]; if (p != null && !p.isDeleted) p else null }

    override suspend fun getPlace(id: String): Place? {
        val p = places.value[id]
        return if (p != null && !p.isDeleted) p else null
    }

    override suspend fun hasPlaceWithNormalizedName(
        normalizedName: String,
        excludePlaceId: String?,
    ): Boolean =
        places.value.values.any { p ->
            !p.isDeleted && p.id != excludePlaceId && normalizeForComparison(p.name) == normalizedName
        }

    override suspend fun savePlace(place: Place, firstVisitAt: Long?): String {
        val timestamp = now()
        val isNew = place.id.isBlank()
        val placeId = if (isNew) idGenerator.newId() else place.id
        val currentUid = authService.getCurrentUserId() ?: ""

        // withDerivedCover(): Room을 쓰는 쪽은 매퍼가 이걸 해 주는데 웹에는 그 관문이 없다.
        // 빠뜨렸더니 가게 갤러리에서 대표 사진을 지워도 imageFileName이 그대로 남아, 방금
        // 삭제한 파일을 가리키는 행이 동기화됐다.
        val savedPlace = place.copy(
            id = placeId,
            createdAt = if (isNew) timestamp else place.createdAt,
            updatedAt = timestamp,
            version = if (isNew) 1L else place.version + 1,
            syncState = SyncState.PENDING_UPSERT,
            ownerId = if (isNew || place.ownerId.isBlank()) currentUid else place.ownerId,
        ).withDerivedCover()

        updatePlaces(places.value + (placeId to savedPlace))
        enqueueSyncItem(placeId, SyncAction.UPSERT)

        if (isNew && savedPlace.status == PlaceStatus.VISITED) {
            addVisit(placeId, visitedAt = firstVisitAt ?: timestamp, note = "")
        }

        return placeId
    }

    override suspend fun toggleLiked(id: String) {
        val place = getPlace(id) ?: return
        savePlace(place.copy(liked = !place.liked))
    }

    override suspend fun deletePlace(id: String) {
        val place = getPlace(id) ?: return
        val timestamp = now()
        val toDelete = place.copy(
            isDeleted = true,
            syncState = SyncState.PENDING_DELETE,
            updatedAt = timestamp,
            version = place.version + 1
        )
        updatePlaces(places.value + (id to toDelete))
        enqueueSyncItem(id, SyncAction.DELETE)
    }

    override fun observeVisits(placeId: String): Flow<List<Visit>> =
        visits.map { allVisits ->
            allVisits.values.filter { it.placeId == placeId && !it.isDeleted }.sortedBy { it.visitedAt }
        }

    override fun observeAllVisits(): Flow<List<Visit>> =
        visits.map { allVisits -> allVisits.values.filter { !it.isDeleted }.sortedBy { it.visitedAt } }

    override suspend fun addVisit(
        placeId: String,
        visitedAt: Long,
        note: String?,
        emotion: String?,
        imageFileName: String?,
        photos: List<PhotoRef>,
    ): String {
        val timestamp = now()
        val currentUid = authService.getCurrentUserId() ?: ""
        val visitId = idGenerator.newId()
        val newVisit = Visit(
            id = visitId,
            placeId = placeId,
            note = note?.trim()?.ifBlank { null },
            visitedAt = visitedAt,
            emotion = emotion,
            imageFileName = imageFileName,
            // 사진 한 장을 목록에도 넣어 둔다. 안 그러면 이 방문의 사진이 멀티 사진 화면에
            // 안 보인다 — imageFileName은 대표 사진 자리일 뿐 목록의 출처가 아니다.
            photos = photos.ifEmpty {
                listOfNotNull(imageFileName?.takeIf { it.isNotBlank() }?.let { PhotoRef(it) })
            },
            ownerId = currentUid,
            syncState = SyncState.PENDING_UPSERT,
            createdAt = timestamp,
            updatedAt = timestamp,
            // 위가 imageFileName → photos 방향만 채우므로 반대 방향은 여기서 맞춘다. 없을 때
            // 웹에서 기록한 방문은 사진이 있어도 imageFileName이 계속 null이었다.
        ).withDerivedCover()
        updateVisits(visits.value + (visitId to newVisit))
        // Room 쪽과 같은 규칙: "Want to go"에 첫 방문이 붙으면 장소를 "Been"으로 올린다.
        val place = getPlace(placeId)
        if (place != null && place.status == PlaceStatus.WISHLIST) {
            savePlace(place.copy(status = PlaceStatus.VISITED))
        }
        return visitId
    }

    override suspend fun getPlaceIncludingDeleted(id: String): Place? = places.value[id]

    override suspend fun savePlaceFromRemote(place: Place) {
        // Mirrors PlaceRepositoryImpl: pullSync's version/updatedAt check ran against a `places`
        // snapshot read *before* this suspend call — a concurrent local edit may have committed
        // since and be newer than that stale read. Re-run the same LWW check against the row as
        // it stands right now: only overwrite (and only clear the queue) when the remote copy
        // still wins against the current row, not the one pullSync happened to read (backlog
        // 0-10, docs/HOTPL_MASTER.md §14).
        val current = places.value[place.id]
        val remoteStillWins = current == null ||
            place.version > current.version ||
            (place.version == current.version && place.updatedAt > current.updatedAt)
        if (!remoteStillWins) return

        // The remote copy just overwrote whatever local change a pending queue item was
        // enqueued for — re-pushing it would send a version the server already has, which the
        // Firestore rules deny forever.
        val staleQueueIds = syncQueue.value.filterValues { it.entityId == place.id }.keys
        if (staleQueueIds.isNotEmpty()) {
            updateSyncQueue(syncQueue.value - staleQueueIds)
        }
        updatePlaces(places.value + (place.id to place))
    }

    // ── PhotoSyncQueue ──────────────────────────────────────────────────────
    // Same queue map as the place rows; entityType tells the push loop which is which.
    // Latest action per file wins (see RoomPhotoSyncQueue for the reasoning).

    override suspend fun enqueueUpload(fileName: String) = enqueuePhoto(fileName, SyncAction.UPSERT)

    override suspend fun enqueueDelete(fileName: String) = enqueuePhoto(fileName, SyncAction.DELETE)

    private fun enqueuePhoto(fileName: String, action: SyncAction) {
        val currentQueue = syncQueue.value
            .filterValues { !(it.entityType == SYNC_ENTITY_PHOTO && it.entityId == fileName) }
            .toMutableMap()
        val newId = ++queueSeq
        currentQueue[newId] = JsSyncQueueEntry(
            queueId = newId,
            entityType = SYNC_ENTITY_PHOTO,
            entityId = fileName,
            action = action.name,
            createdAt = now(),
        )
        updateSyncQueue(currentQueue)
    }

    override suspend fun photoFileNamesInUse(): List<String> {
        val fromPlaces = places.value.values.filter { !it.isDeleted }
            .flatMap { it.photos.map { p -> p.fileName } + listOfNotNull(it.imageFileName) }
        val fromVisits = visits.value.values.filter { !it.isDeleted }
            .flatMap { it.photos.map { p -> p.fileName } + listOfNotNull(it.imageFileName) }
        return (fromPlaces + fromVisits).filter { it.isNotBlank() }.distinct()
    }

    override suspend fun getPendingSyncItems(): List<SyncQueueItem> =
        syncQueue.value.values.map { it.toSyncQueueItem() }

    override suspend fun getPendingSyncItems(limit: Int): List<SyncQueueItem> =
        syncQueue.value.values
            .sortedBy { it.createdAt }
            .take(limit)
            .map { it.toSyncQueueItem() }

    override suspend fun deleteSyncItem(queueId: Long) {
        if (syncQueue.value.containsKey(queueId)) {
            updateSyncQueue(syncQueue.value - queueId)
        }
    }

    override suspend fun markPlaceSynced(id: String, syncState: SyncState) {
        updatePlaces(
            places.value.mapValues { (key, place) ->
                if (key == id) place.copy(syncState = syncState) else place
            },
        )
    }

    override suspend fun brandLocalPlacesWithUser(uid: String) {
        if (uid.isBlank()) return
        val currentPlaces = places.value.toMutableMap()
        var updated = false
        for ((id, place) in currentPlaces) {
            if (place.ownerId.isBlank()) {
                currentPlaces[id] = place.copy(ownerId = uid, syncState = SyncState.PENDING_UPSERT)
                enqueueSyncItem(id, SyncAction.UPSERT)
                updated = true
            }
        }
        if (updated) {
            updatePlaces(currentPlaces)
        }
    }

    override suspend fun strandedOwnerIds(currentUid: String): List<String> =
        if (currentUid.isBlank()) emptyList()
        else places.value.values.map { it.ownerId }.filter { it.isNotBlank() && it != currentUid }.distinct()

    override suspend fun rebrandPlaces(
        fromUid: String,
        toUid: String,
        reservedIds: Set<String>,
        duplicateKeys: Map<DuplicateKey, String>,
    ) {
        if (fromUid.isBlank() || toUid.isBlank() || fromUid == toUid) return
        val currentPlaces = places.value.toMutableMap()
        var updated = false

        // `.entries.toList()` is not a real snapshot on Kotlin/JS: the Map.Entry objects it
        // holds still read through to the live backing map, so mutating currentPlaces (remove /
        // put below) during this loop throws ConcurrentModificationException on the next
        // iteration's entry access. Copying out plain (id, place) pairs detaches them fully.
        for ((id, place) in currentPlaces.map { it.key to it.value }) {
            if (place.ownerId != fromUid) continue
            val lat = place.latitude
            val lng = place.longitude
            // takeIf { it != id }: a self-match means there is nothing to adopt — see the
            // matching comment in PlaceRepositoryImpl.rebrandPlaces.
            val duplicateOfId = if (lat != null && lng != null) {
                duplicateKeys[DuplicateKey(normalizeForComparison(place.name), lat, lng)]
                    ?.takeIf { it != id }
            } else {
                null
            }
            // The rebrand moves the place to a brand-new Firestore document path
            // (users/{toUid}/places/{id}) — a *create* there, not an update. The prod
            // rules require version == 1 on create, so the old version (2, 3, …) would
            // make every rebranded push fail with "Missing or insufficient permissions".
            // Reset to 1 and bump updatedAt so the new doc is fresh from the new account.
            //
            // Unless this place adopts an existing remote document (duplicateOfId != null):
            // there is no new doc to create, so it stays SYNCED with version = 0, guaranteeing
            // the next pull's LWW check always prefers the existing remote data over this
            // placeholder — see the matching comment in PlaceRepositoryImpl.rebrandPlaces.
            val rebranded = place.copy(
                ownerId = toUid,
                version = if (duplicateOfId != null) 0L else 1L,
                updatedAt = now(),
                syncState = if (duplicateOfId != null) SyncState.SYNCED else SyncState.PENDING_UPSERT,
            )
            val newId = duplicateOfId ?: if (id in reservedIds) idGenerator.newId() else null
            if (newId != null) {
                currentPlaces.remove(id)
                currentPlaces[newId] = rebranded.copy(id = newId)
                // Remove any pending queue entry for the old id.
                val oldQueueEntries = syncQueue.value.values.filter { it.entityId == id }
                if (oldQueueEntries.isNotEmpty()) {
                    val updatedQueue = syncQueue.value.toMutableMap()
                    for (entry in oldQueueEntries) updatedQueue.remove(entry.queueId)
                    updateSyncQueue(updatedQueue)
                }
                // A place adopting an existing remote document has nothing new to push.
                if (duplicateOfId == null) enqueueSyncItem(newId, SyncAction.UPSERT)
            } else {
                currentPlaces[id] = rebranded
                enqueueSyncItem(id, SyncAction.UPSERT)
            }
            updated = true
        }
        if (updated) {
            updatePlaces(currentPlaces)
        }
    }

    override suspend fun resetPlaceForRemoteCreate(placeId: String) {
        val place = places.value[placeId] ?: return
        if (place.version == 1L) return
        updatePlaces(
            places.value + (placeId to place.copy(
                version = 1L,
                updatedAt = now(),
                syncState = SyncState.PENDING_UPSERT,
            ))
        )
        println("HotPl rebrand-fix: reset place version ${place.version} -> 1 (stale-version recovery)")
    }

    override suspend fun countPlacesOwnedBy(uid: String): Int {
        if (uid.isBlank()) return 0
        return places.value.values.count { it.ownerId == uid && !it.isDeleted }
    }

    override suspend fun discardPlacesOwnedBy(uid: String) {
        if (uid.isBlank()) return
        val toRemove = places.value.filterValues { it.ownerId == uid }.keys
        if (toRemove.isEmpty()) return
        updatePlaces(places.value - toRemove)
        updateVisits(visits.value.filterValues { it.placeId !in toRemove })
        updateSyncQueue(syncQueue.value.filterValues { it.entityId !in toRemove })
    }

    override suspend fun getVisitIncludingDeleted(id: String): Visit? = visits.value[id]

    override suspend fun updateVisit(visit: Visit) {
        val timestamp = now()
        val toSave = visit.copy(
            updatedAt = timestamp,
            version = visit.version + 1,
            syncState = SyncState.PENDING_UPSERT,
        ).withDerivedCover()
        updateVisits(visits.value + (visit.id to toSave))
    }

    override suspend fun saveVisitFromRemote(visit: Visit) {
        updateVisits(visits.value + (visit.id to visit))
    }

    override suspend fun deleteVisit(id: String) {
        val visit = visits.value[id] ?: return
        val timestamp = now()
        val tombstoned = visit.copy(
            isDeleted = true,
            syncState = SyncState.PENDING_DELETE,
            updatedAt = timestamp,
            version = visit.version + 1,
        )
        updateVisits(visits.value + (id to tombstoned))
    }

    override suspend fun getPendingVisitSyncItems(limit: Int): List<Visit> =
        visits.value.values.filter { it.syncState != SyncState.SYNCED }.take(limit)

    override suspend fun markVisitSynced(id: String, syncState: SyncState) {
        visits.value[id]?.let { visit ->
            updateVisits(visits.value + (id to visit.copy(syncState = syncState)))
        }
    }

    override suspend fun brandLocalVisitsWithUser(uid: String) {
        if (uid.isBlank()) return
        val currentVisits = visits.value.toMutableMap()
        var updated = false
        for ((id, visit) in currentVisits) {
            if (visit.ownerId.isBlank()) {
                currentVisits[id] = visit.copy(ownerId = uid, syncState = SyncState.PENDING_UPSERT)
                updated = true
            }
        }
        if (updated) updateVisits(currentVisits)
    }

    override suspend fun strandedVisitOwnerIds(currentUid: String): List<String> =
        if (currentUid.isBlank()) emptyList()
        else visits.value.values.map { it.ownerId }.filter { it.isNotBlank() && it != currentUid }.distinct()

    override suspend fun rebrandVisitOwners(fromUid: String, toUid: String) {
        if (fromUid.isBlank() || toUid.isBlank() || fromUid == toUid) return
        val currentVisits = visits.value.toMutableMap()
        var updated = false
        for ((id, visit) in currentVisits) {
            if (visit.ownerId == fromUid) {
                currentVisits[id] = visit.copy(ownerId = toUid, syncState = SyncState.PENDING_UPSERT)
                updated = true
            }
        }
        if (updated) updateVisits(currentVisits)
    }

    internal fun getAllPlacesRaw(): List<Place> {
        return places.value.values.toList()
    }

    internal fun getAllVisitsRaw(): List<Visit> {
        return visits.value.values.toList()
    }

    internal fun setAll(newPlaces: List<Place>, newVisits: List<Visit>) {
        places.value = newPlaces.associateBy { it.id }
        visits.value = newVisits.associateBy { it.id }
        persist()
    }
}

private fun JsSyncQueueEntry.toSyncQueueItem(): SyncQueueItem = SyncQueueItem(
    queueId = queueId,
    entityType = entityType,
    entityId = entityId,
    action = runCatching { SyncAction.valueOf(action) }.getOrDefault(SyncAction.UPSERT),
    createdAt = createdAt,
)

private class DummyAuthService : AuthService {
    override val currentUserId: Flow<String?> = flowOf(null)
    override suspend fun getCurrentUserId(): String? = null
    override suspend fun ensureSignedIn(): String = ""
    override suspend fun linkWithGoogle(idToken: String, accessToken: String?): Result<Unit> = Result.success(Unit)
    override suspend fun linkWithApple(idToken: String, rawNonce: String): Result<Unit> = Result.success(Unit)
}
